<template>
  <list-page-shell>
    <p class="mail-intro">{{ $t('emailConfigPage.intro') }}</p>
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="140px"
      class="mail-form"
      size="medium"
    >
      <el-form-item :label="$t('emailConfigPage.smtpHost')" prop="smtpHost">
        <el-input v-model="form.smtpHost" :placeholder="$t('emailConfigPage.smtpHostPh')" clearable />
      </el-form-item>
      <el-form-item :label="$t('emailConfigPage.smtpPort')" prop="smtpPort">
        <el-input-number v-model="form.smtpPort" :min="1" :max="65535" controls-position="right" />
      </el-form-item>
      <el-form-item :label="$t('emailConfigPage.smtpUsername')" prop="smtpUsername">
        <el-input v-model="form.smtpUsername" :placeholder="$t('emailConfigPage.smtpUsernamePh')" clearable />
      </el-form-item>
      <el-form-item :label="$t('emailConfigPage.smtpPassword')" prop="smtpPassword">
        <el-input
          v-model="form.smtpPassword"
          type="password"
          show-password
          autocomplete="new-password"
          :placeholder="passwordPlaceholder"
          clearable
        />
      </el-form-item>
      <el-form-item :label="$t('emailConfigPage.fromEmail')">
        <el-input v-model="form.fromEmail" :placeholder="$t('emailConfigPage.fromEmailPh')" clearable />
      </el-form-item>
      <el-form-item :label="$t('emailConfigPage.fromPersonal')">
        <el-input v-model="form.fromPersonal" :placeholder="$t('emailConfigPage.fromPersonalPh')" clearable />
      </el-form-item>
      <el-form-item :label="$t('emailConfigPage.ssl')">
        <el-switch v-model="form.sslEnabled" :active-text="$t('emailConfigPage.sslOn')" />
      </el-form-item>
      <el-form-item :label="$t('emailConfigPage.starttls')">
        <el-switch v-model="form.starttlsEnabled" :active-text="$t('emailConfigPage.starttlsOn')" />
      </el-form-item>
      <el-form-item :label="$t('emailConfigPage.publicBase')">
        <el-input v-model="form.publicQuoteBaseUrl" :placeholder="$t('emailConfigPage.publicBasePh')" clearable />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="saving" @click="submit">{{ $t('emailConfigPage.save') }}</el-button>
      </el-form-item>
    </el-form>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import { getUserMailProfile, saveUserMailProfile } from '@/api/user'

export default {
  name: 'MailSettings',
  components: { ListPageShell },
  data() {
    return {
      passwordSet: false,
      saving: false,
      form: {
        smtpHost: '',
        smtpPort: 587,
        smtpUsername: '',
        smtpPassword: '',
        fromEmail: '',
        fromPersonal: '',
        sslEnabled: false,
        starttlsEnabled: true,
        publicQuoteBaseUrl: ''
      }
    }
  },
  computed: {
    passwordPlaceholder() {
      return this.passwordSet ? this.$t('emailConfigPage.passwordKeep') : this.$t('emailConfigPage.passwordPh')
    },
    rules() {
      const vm = this
      return {
        smtpHost: [{ required: true, message: vm.$t('emailConfigPage.smtpHostRequired'), trigger: 'blur' }],
        smtpPort: [{ required: true, message: vm.$t('emailConfigPage.smtpPortRequired'), trigger: 'change' }],
        smtpUsername: [{ required: true, message: vm.$t('emailConfigPage.smtpUsernameRequired'), trigger: 'blur' }],
        smtpPassword: [
          {
            validator(rule, value, callback) {
              if (vm.passwordSet) {
                return callback()
              }
              if (!value || !String(value).trim()) {
                return callback(new Error(vm.$t('emailConfigPage.passwordRequired')))
              }
              callback()
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      try {
        const r = await getUserMailProfile()
        if (!r || r.code !== 200) {
          this.$message.error((r && r.msg) || this.$t('emailConfigPage.loadFail'))
          return
        }
        const d = r.data || {}
        this.passwordSet = !!d.passwordSet
        this.form.smtpHost = d.smtpHost || ''
        this.form.smtpPort = d.smtpPort != null ? d.smtpPort : 587
        this.form.smtpUsername = d.smtpUsername || ''
        this.form.smtpPassword = ''
        this.form.fromEmail = d.fromEmail || ''
        this.form.fromPersonal = d.fromPersonal || ''
        this.form.sslEnabled = !!d.sslEnabled
        this.form.starttlsEnabled = d.starttlsEnabled !== false
        this.form.publicQuoteBaseUrl = d.publicQuoteBaseUrl || ''
        this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
      } catch (e) {
        this.$message.error(e.message || this.$t('emailConfigPage.loadFail'))
      }
    },
    submit() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            smtpHost: this.form.smtpHost,
            smtpPort: this.form.smtpPort,
            smtpUsername: this.form.smtpUsername,
            smtpPassword: this.form.smtpPassword,
            fromEmail: this.form.fromEmail,
            fromPersonal: this.form.fromPersonal,
            sslEnabled: this.form.sslEnabled,
            starttlsEnabled: this.form.starttlsEnabled,
            publicQuoteBaseUrl: this.form.publicQuoteBaseUrl
          }
          const r = await saveUserMailProfile(payload)
          if (!r || r.code !== 200) {
            this.$message.error((r && r.msg) || this.$t('emailConfigPage.saveFail'))
            return
          }
          this.passwordSet = !!(r.data && r.data.passwordSet)
          this.form.smtpPassword = ''
          this.$message.success(this.$t('emailConfigPage.saveOk'))
          this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
        } catch (e) {
          this.$message.error(e.message || this.$t('emailConfigPage.saveFail'))
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.mail-intro {
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 20px;
  max-width: 720px;
}

.mail-form {
  max-width: 640px;
}
</style>
